import { useMemo, useRef, useState } from 'react';
import styles from './index.module.less';
import { Affix, Dropdown, MenuProps, Tooltip } from 'antd';
import { useUserProvider } from '@/context/UserProvider';
import { InsProfileModel } from '@/types/home';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { formatNumbers } from '@/shared/utils';
import { ExportOutlined } from '@ant-design/icons';
import { FacebookShareButton, TwitterShareButton } from 'react-share';
import dynamic from 'next/dynamic';
import { useWindowSize } from 'react-use';

const InstagramActivityTracking = dynamic(() => import('./InstagramActivityTracking'), {
  ssr: true,
});
const DailyNewFollower = dynamic(() => import('../dailyNewFollowers'), { ssr: true });
const HisToricalStats = dynamic(() => import('../historyStats'), { ssr: true });
const LocalImage = dynamic(() => import('@/components/Common/LocalImage'), { ssr: true });

export default function SocialDashboard({
  dataInfo,
  bottomVisible,
  onAddAccount,
}: {
  dataInfo?: InsProfileModel;
  bottomVisible?: boolean;
  onAddAccount?: (value?: string) => void;
}) {
  const { isLogin } = useUserProvider();
  const { t } = useTranslation();
  const currentHref = useMemo(
    () => process.env.NEXT_APP_WEB_HOST + '/instagram/' + dataInfo?.media_name,
    [dataInfo?.media_name]
  );

  const [activeTab, setActiveTab] = useState('about');
  const aboutRef = useRef<HTMLDivElement>(null);
  const newsRef = useRef<HTMLDivElement>(null);
  const data1Ref = useRef<HTMLDivElement>(null);
  const data2Ref = useRef<HTMLDivElement>(null);
  const data3Ref = useRef<HTMLDivElement>(null);

  const { width } = useWindowSize();

  const items: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <div className={styles.shareContainer} style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
          <span>{t('Share')}:</span>

          <FacebookShareButton url={currentHref}>
            <Image
              className={styles.iconButton}
              src={'/images/starInfo/facebook-icon.webp'}
              alt={'facebook'}
              width={30}
              height={30}
            />
          </FacebookShareButton>

          <TwitterShareButton title={''} url={currentHref}>
            <Image
              className={styles.iconButton}
              src={'/images/starInfo/x-icon.webp'}
              alt={'twitter'}
              width={30}
              height={30}
            />
          </TwitterShareButton>
        </div>
      ),
    },
  ];

  const shareContainer = useMemo(() => {
    return (
      <Dropdown menu={{ items }} placement="top" arrow>
        <button className={styles.shareButton}>
          <ExportOutlined /> {t('Share')}
        </button>
      </Dropdown>
    );
  }, []);

  const changeListData = useMemo(() => {
    return [
      {
        title: t('Monthly Growth'),
        value: formatNumbers(dataInfo?.monthly_num ?? 0),
        icon: 'monthly-growth-icon.webp',
        bgColor: '#F1EFFF',
      },
      {
        title: t('Followers'),
        value: formatNumbers(dataInfo?.follower_num ?? 0),
        icon: 'followers-icon.webp',
        bgColor: '#FFEFEF',
      },
      {
        title: t('Posts'),
        value: formatNumbers(dataInfo?.post_num ?? 0),
        icon: 'posts-icon.webp',
        bgColor: '#E5F4E5',
      },
      {
        title: t('Following'),
        value: formatNumbers(dataInfo?.following_num ?? 0),
        icon: 'following-icon.webp',
        bgColor: '#FBF3DE',
      },
    ];
  }, [dataInfo?.monthly_num, dataInfo?.follower_num, dataInfo?.post_num, dataInfo?.following_num]);

  const scrollToRef = (ref: React.RefObject<HTMLDivElement>) => {
    // ref.current?.scrollIntoView({ behavior: 'smooth' })
    if (ref.current) {
      const offset = isLogin ? 95 : 140; // 偏移量
      const elementPosition = ref.current.getBoundingClientRect().top; // 元素相对视口的位置
      const scrollPosition = window.scrollY + elementPosition - offset; // 计算目标滚动位置

      window.scrollTo({
        top: scrollPosition,
        behavior: 'smooth', // 平滑滚动
      });
    }
  };

  const handleTabClick = (tabValue: string, ref: React.RefObject<HTMLDivElement>) => {
    setActiveTab(tabValue);
    scrollToRef(ref);
  };

  const getTopCards = useMemo(() => {
    return (
      <div className={styles.statsGrid}>
        {changeListData?.map((item, index) => {
          return (
            <div className={`${styles.statsCard}`} key={index} style={{ backgroundColor: item?.bgColor }}>
              <div className={styles.statsLabel}>
                <Image
                  src={`/images/starInfo/${item?.icon}`}
                  alt={item?.title}
                  width={24}
                  height={24}
                  style={{ marginRight: '6px' }}
                />
                <span>{item?.title}</span>
                {/* {item?.tip && <Tooltip title={item?.tip} className={styles.tip}>
              <QuestionCircleOutlined />
            </Tooltip>} */}
              </div>
              <div className={styles.statsValue}>{item?.value}</div>
            </div>
          );
        })}
      </div>
    );
  }, [changeListData]);

  const getTabsContent = useMemo(() => {
    const tabsContent = (
      <div className={styles.tabList}>
        <button
          onClick={() => handleTabClick('about', aboutRef)}
          className={`${styles.tabTrigger} ${activeTab === 'about' ? styles.active : ''}`}
        >
          {t('Overview')}
        </button>
        <button
          onClick={() => handleTabClick('news', newsRef)}
          className={`${styles.tabTrigger} ${activeTab === 'news' ? styles.active : ''}`}
        >
          {t('Hot Topics')}
        </button>
        <button
          onClick={() => handleTabClick('data1', data1Ref)}
          className={`${styles.tabTrigger} ${activeTab === 'data1' ? styles.active : ''}`}
        >
          {t('Activity Insights')}
        </button>
        <button
          onClick={() => handleTabClick('data2', data2Ref)}
          className={`${styles.tabTrigger} ${activeTab === 'data2' ? styles.active : ''}`}
        >
          {t('Daily New Followers')}
        </button>
        <button
          onClick={() => handleTabClick('data3', data3Ref)}
          className={`${styles.tabTrigger} ${activeTab === 'data3' ? styles.active : ''}`}
        >
          {t('Historical Stats')}
        </button>
      </div>
    );

    if (bottomVisible) {
      return tabsContent;
    }

    return <Affix offsetTop={isLogin || width < 900 ? 64 : 105}>{tabsContent}</Affix>;
  }, [activeTab, bottomVisible, width]);

  return (
    <div className={styles.dashboard}>
      {/* Stats Section */}
      <div className={styles.webTopContainer}>{getTopCards}</div>

      {/* Tabs Section */}
      <div className={styles.tabsContainer}>
        {getTabsContent}

        <div className={styles.tabContent}>
          <div className={styles.phoneTopContainer}>{getTopCards}</div>
          <div style={{ display: 'none' }}>
            <h2>{t('@xxx Instagram Stats & Growth Trends').replace('xxx', dataInfo?.media_name)}</h2>
            <h3>{t('Latest Instagram Follower Count & Growth for @xxx').replace('xxx', dataInfo?.media_name)}</h3>
            <h3>{t('See Live Follower Count Updates')}</h3>
            <h3>{t('@xxx’s Daily Instagram Follower Growth').replace('xxx', dataInfo?.media_name)}</h3>
            <h3>{t('@xxx’s Instagram Follower Growth History').replace('xxx', dataInfo?.media_name)}</h3>
          </div>
          <div ref={aboutRef}>
            <div className={styles.card} style={{}}>
              <div className={styles.newsTitle}>@{dataInfo?.media_name}</div>
              <p className={styles.newsExcerpt}>{dataInfo?.introduction}</p>
              <p className={styles.newsExcerpt}>{dataInfo?.about_analyze}</p>
            </div>
          </div>
          <div style={{ display: 'none' }}>
            <h2>{t('Instagram Follower Count Tracking')}</h2>
            <h3>{t('What It Tracks: Follower Growth, Trends, and Stats')}</h3>
            <h3>{t('Curious About Any Influencer’s Real-Time Instagram Follower Count? Track It Here')}</h3>
            <h2>
              {t('@xxx’s Instagram Activity & Live Follower Count Insights').replace('xxx', dataInfo?.media_name)}
            </h2>
            <h3>{t('Get In-Depth Instagram Follower Stats & History')}</h3>
            <h3>{t('AI-Powered Follower Count & Engagement Tracking')}</h3>
            <h3>{t('See @xxx’s Live Instagram Follower Count').replace('xxx', dataInfo?.media_name)}</h3>
            <h2>{t('Trending Instagram Accounts & Instagram Followers Count History')}</h2>
            <h3>{t('Popular Influencers With Rapid Instagram Follower Growth')}</h3>
            <h3>{t('Curious About Any Influencer’s Real-Time Instagram Follower Count? Track It Here')}</h3>
            <h3>{t('Influencers Gaining or Losing Followers Fast')}</h3>
            <h2>{t('Not Found? Add an Account to Track Live Instagram Follower Count')}</h2>
            <h3>{t('See the Latest Instagram Activity of the Person You Care About')}</h3>
            <h3>{t('Get In-Depth AI Analysis and Instagram Behavior Insights')}</h3>
            <h3>{t('Anonymous Instagram Story Viewer & Live Follower Count Tracking')}</h3>
          </div>
          <div ref={newsRef}>
            <div className={styles.card}>
              <div className={styles.newsHeader}>
                <div className={styles.newsTitle}>
                  {t("@x111x's Hot Topics").replace('x111x', dataInfo?.media_name)}
                </div>
                {shareContainer}
              </div>

              <div style={{ marginBottom: '1.5rem' }} className={styles.newsExcerpt}>
                {dataInfo?.news_analyze}
              </div>

              {dataInfo?.news_data?.length > 0 && (
                <div className={styles.newsContainer}>
                  <div className={styles.newTitle}>{t('News')}</div>
                  {dataInfo?.news_data?.map((item, index) => {
                    return (
                      <div
                        className={styles.newsItem}
                        key={index}
                        onClick={() => {
                          window.open(item.source_link, '_blank');
                        }}
                      >
                        <div className={styles.newsContent}>
                          <div className={styles.logoContainer}>
                            {item?.source_logo && (
                              <LocalImage
                                src={item?.source_logo}
                                alt={''}
                                width={18}
                                height={18}
                                style={{ width: '18px', height: '18px', borderRadius: '50%' }}
                              />
                            )}
                            <span className={`${styles.newsSource}`}>{item?.source}</span>
                          </div>
                          <div className={styles.newsHeadline}>{item?.title}</div>
                          {width > 900 && <p className={styles.newsExcerpt}>{item?.snippet}</p>}
                          <span className={styles.newsDate}>{item?.date}</span>
                        </div>

                        <div className={styles.newsImage}>
                          {item?.image_url && (
                            <LocalImage src={item?.image_url} alt={item?.source} style={{ borderRadius: '8px' }} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* <div style={{ marginTop: "1.25rem" }}>
                <HotTopics keyword={dataInfo?.media_name} />
              </div> */}
            </div>
          </div>

          <div ref={data1Ref}>
            <div className={styles.card}>
              <InstagramActivityTracking mediaName={dataInfo?.media_name} onAddAccount={onAddAccount} />
            </div>
          </div>

          <div ref={data2Ref}>
            <div className={styles.card}>
              <div className={styles.newsHeader}>
                <div className={styles.newsTitle}>
                  {t("@x111x's Instagram Stats for Follower Growth").replace('x111x', dataInfo?.media_name)}
                </div>
                {shareContainer}
              </div>

              <div>
                <div>
                  <span style={{ fontSize: '20px', fontWeight: '900' }}>
                    {dataInfo?.data_list?.at(0).daily_new_follower_num > 0
                      ? `+${dataInfo?.data_list?.at(0).daily_new_follower_num}`
                      : dataInfo?.data_list?.at(0).daily_new_follower_num}
                  </span>
                </div>
                <p className={styles.newsExcerpt}>{dataInfo?.follower_analyze}</p>
              </div>

              <DailyNewFollower mediaName={dataInfo?.media_name} dataInfo={dataInfo?.data_list} />

              <div className={styles.addAccountContainer} onClick={() => onAddAccount && onAddAccount()}>
                {t('+ Add account for Instagram report insights and in-depth analysis')}
              </div>
            </div>
          </div>

          <div ref={data3Ref}>
            <div className={styles.card} style={{ borderBottom: 'none' }}>
              <div className={styles.newsHeader}>
                <div className={styles.newsTitle}>
                  {t("@x111x's Historical Stats").replace('x111x', dataInfo?.media_name)}
                </div>
                {shareContainer}
              </div>

              <p className={styles.newsExcerpt}>{dataInfo?.history_analyze}</p>

              <HisToricalStats dataInfo={dataInfo?.data_list} />

              <div className={styles.addAccountContainer} onClick={() => onAddAccount && onAddAccount()}>
                {t('+ Add account for Instagram report insights and in-depth analysis')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
